import { useEffect, useState } from "react";
import { Plus, Library, Pencil, Trash2, Save, Share2, Globe } from "lucide-react";
import api from "../lib/api";
import AppLayout from "../components/AppLayout";
import LibraryShareDialog from "../components/LibraryShareDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";

const emptyForm = { name: "", description: "", templates: [""], variables: {} };

export default function CaptionLibrariesPage() {
  const [libraries, setLibraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLib, setShareLib] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/caption-libraries");
      setLibraries(data);
    } catch {
      toast.error("Failed to load libraries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (lib) => {
    setEditing(lib);
    setForm({
      name: lib.name,
      description: lib.description || "",
      templates: lib.templates?.length ? [...lib.templates] : [""],
      variables: { ...(lib.variables || {}) },
    });
    setOpen(true);
  };

  const setTemplate = (i, v) => {
    const next = [...form.templates];
    next[i] = v;
    setForm({ ...form, templates: next });
  };
  const addTemplate = () => setForm({ ...form, templates: [...form.templates, ""] });
  const removeTemplate = (i) =>
    setForm({ ...form, templates: form.templates.filter((_, j) => j !== i) });

  const varEntries = Object.entries(form.variables || {});
  const addVar = () => {
    let key = "event_name";
    let i = 2;
    while (Object.prototype.hasOwnProperty.call(form.variables, key)) key = `event_name_${i++}`;
    setForm({ ...form, variables: { ...form.variables, [key]: "" } });
  };
  const renameVar = (oldK, newK) => {
    if (oldK === newK || !newK.trim()) return;
    const next = { ...form.variables };
    next[newK] = next[oldK];
    delete next[oldK];
    setForm({ ...form, variables: next });
  };
  const setVar = (k, v) => setForm({ ...form, variables: { ...form.variables, [k]: v } });
  const removeVar = (k) => {
    const next = { ...form.variables };
    delete next[k];
    setForm({ ...form, variables: next });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      templates: form.templates.map((t) => t.trim()).filter(Boolean),
      variables: form.variables || {},
    };
    try {
      if (editing) {
        await api.put(`/caption-libraries/${editing.id}`, payload);
        toast.success("Library updated");
      } else {
        await api.post("/caption-libraries", payload);
        toast.success("Library created");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (lib) => {
    if (!window.confirm(`Delete "${lib.name}"? This won't affect journals that already imported it.`)) return;
    try {
      await api.delete(`/caption-libraries/${lib.id}`);
      toast.success("Library deleted");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <AppLayout>
      <div className="p-8 max-w-5xl">
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <div className="label-eyebrow mb-1">Workspace</div>
            <h1 className="text-3xl font-display font-bold mb-1 flex items-center gap-2">
              <Library className="w-7 h-7 text-blue-400" /> Caption Libraries
            </h1>
            <p className="text-zinc-400 text-sm">
              Reusable template sets — import any library into a journal so empty captions
              fall back to your prepared messages.
            </p>
          </div>
          <Button
            data-testid="new-library-btn"
            onClick={openNew}
            className="bg-blue-600 hover:bg-blue-500 shrink-0"
          >
            <Plus className="w-4 h-4 mr-2" /> New library
          </Button>
        </div>

        {loading ? (
          <div className="text-zinc-500">Loading…</div>
        ) : libraries.length === 0 ? (
          <div className="border border-dashed border-zinc-800 rounded-md p-10 text-center">
            <Library className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 mb-1">No libraries yet.</p>
            <p className="text-zinc-500 text-sm mb-5">
              Create one with messages like “Good luck to everyone in the {"{event_name}"}!” and reuse it across journals.
            </p>
            <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-500">
              <Plus className="w-4 h-4 mr-2" /> Create your first library
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {libraries.map((lib) => (
              <div
                key={lib.id}
                data-testid={`library-card-${lib.id}`}
                className="bg-zinc-900 border border-zinc-800 rounded-md p-4 group hover:border-zinc-700 transition-colors"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="font-display font-semibold truncate">{lib.name}</div>
                    {lib.description && (
                      <div className="text-xs text-zinc-500 truncate">{lib.description}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      data-testid={`library-share-${lib.id}`}
                      onClick={() => { setShareLib(lib); setShareOpen(true); }}
                      className="text-zinc-400 hover:text-blue-300 p-1.5 rounded hover:bg-zinc-800 relative"
                      title={lib.is_public ? "Sharing on" : "Share"}
                    >
                      <Share2 className="w-3.5 h-3.5" />
                      {lib.is_public && (
                        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400" />
                      )}
                    </button>
                    <button
                      type="button"
                      data-testid={`library-edit-${lib.id}`}
                      onClick={() => openEdit(lib)}
                      className="text-zinc-400 hover:text-zinc-100 p-1.5 rounded hover:bg-zinc-800"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      data-testid={`library-delete-${lib.id}`}
                      onClick={() => remove(lib)}
                      className="text-zinc-400 hover:text-red-400 p-1.5 rounded hover:bg-zinc-800"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="text-[11px] text-zinc-500 mb-2 flex items-center gap-2">
                  <span>
                    {lib.templates?.length || 0} template{(lib.templates?.length || 0) === 1 ? "" : "s"}
                    {" · "}
                    {Object.keys(lib.variables || {}).length} variable{Object.keys(lib.variables || {}).length === 1 ? "" : "s"}
                  </span>
                  {lib.is_public && (
                    <span
                      data-testid={`library-shared-badge-${lib.id}`}
                      className="inline-flex items-center gap-1 text-emerald-400"
                    >
                      <Globe className="w-3 h-3" /> shared
                    </span>
                  )}
                </div>
                {lib.templates?.length > 0 && (
                  <ul className="space-y-1 text-xs text-zinc-300">
                    {lib.templates.slice(0, 3).map((t, i) => (
                      <li
                        key={i}
                        className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1 line-clamp-2"
                      >
                        {t}
                      </li>
                    ))}
                    {lib.templates.length > 3 && (
                      <li className="text-[10px] text-zinc-600">
                        +{lib.templates.length - 3} more…
                      </li>
                    )}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Edit library" : "New library"}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Templates may include <span className="font-mono">{"{var}"}</span> tokens that are
              filled by variables defined here or on the journal.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1">
              <Label className="label-eyebrow">Name</Label>
              <Input
                data-testid="library-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Hockey 2026"
                required
                className="bg-zinc-950 border-zinc-800"
              />
            </div>
            <div className="space-y-1">
              <Label className="label-eyebrow">Description</Label>
              <Input
                data-testid="library-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="(optional)"
                className="bg-zinc-950 border-zinc-800"
              />
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="label-eyebrow">Default variables</Label>
                <button
                  type="button"
                  data-testid="library-add-var"
                  onClick={addVar}
                  className="text-xs text-blue-400 hover:text-blue-300"
                >
                  + Add variable
                </button>
              </div>
              {varEntries.length === 0 && (
                <p className="text-[10px] text-zinc-500">
                  Variables let templates say <span className="font-mono">{"{event_name}"}</span> — leave blank to set per-journal.
                </p>
              )}
              <div className="space-y-1.5">
                {varEntries.map(([k, v]) => (
                  <div key={k} className="flex items-center gap-1.5">
                    <Input
                      defaultValue={k}
                      onBlur={(e) => renameVar(k, e.target.value.trim())}
                      placeholder="key"
                      className="bg-zinc-950 border-zinc-800 h-8 text-xs font-mono w-32"
                    />
                    <Input
                      value={v || ""}
                      onChange={(e) => setVar(k, e.target.value)}
                      placeholder="default value (optional)"
                      className="bg-zinc-950 border-zinc-800 h-8 text-xs flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => removeVar(k)}
                      className="text-zinc-500 hover:text-red-400 p-1"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="label-eyebrow">Templates ({form.templates.length})</Label>
                <button
                  type="button"
                  data-testid="library-add-template"
                  onClick={addTemplate}
                  className="text-xs text-blue-400 hover:text-blue-300"
                >
                  + Add template
                </button>
              </div>
              <div className="space-y-2">
                {form.templates.map((t, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <span className="text-[10px] text-zinc-600 font-mono pt-1.5 w-4 text-right">{i + 1}.</span>
                    <Textarea
                      data-testid={`library-template-${i}`}
                      value={t}
                      onChange={(e) => setTemplate(i, e.target.value)}
                      rows={2}
                      placeholder="e.g. Good luck to everyone in the {event_name}!"
                      className="bg-zinc-950 border-zinc-800 text-xs flex-1 min-h-0 py-1"
                    />
                    {form.templates.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTemplate(i)}
                        className="text-zinc-500 hover:text-red-400 p-1 mt-1"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button
                type="submit"
                data-testid="library-save-btn"
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-500"
              >
                <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : (editing ? "Save changes" : "Create library")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <LibraryShareDialog
        open={shareOpen}
        onOpenChange={(v) => { setShareOpen(v); if (!v) setShareLib(null); }}
        library={shareLib}
        onUpdate={(updated) => {
          setLibraries((arr) => arr.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
          setShareLib((cur) => (cur ? { ...cur, ...updated } : cur));
        }}
      />
    </AppLayout>
  );
}
